import { Center, Flex, Input, Select, Text } from '@chakra-ui/react';
import { useState } from 'react';

import Modal from '../../../../../../../components/Modal';
import PrimaryButton from '../../../../../../../components/ui/buttons/PrimaryButton';
import { DealMediaTypeEnum } from '../../../../../../../gql/generated/graphql';
import { useFilePicker } from '../../../../../../../hooks';
import { GetLeadByExternalIdPostAcceptanceChecklist200 } from '../../../../../../../services/marketplace/generated/kubb/models/GetLeadByExternalIdPostAcceptanceChecklist';
import { UploadKind, acceptForKind, findChecklistSection } from '../utils';

export type FileTypeOption = {
  kind: UploadKind;
  key: string;
  label: string;
  hint?: string;
  dealOnlyMediaType?: DealMediaTypeEnum;
};

type UploadFileModalProps = {
  isOpen: boolean;
  onClose: () => void;
  checklist?: GetLeadByExternalIdPostAcceptanceChecklist200;
  upload: (
    kind: UploadKind,
    key: string,
    file: File,
    dealOnlyMediaType?: DealMediaTypeEnum,
  ) => Promise<unknown>;
  isProcessing: (key: string) => boolean;
  extraDocumentOptions?: FileTypeOption[];
};

const OTHER_OPTION: FileTypeOption = { kind: 'document', key: 'other', label: 'Other' };

const optionValue = ({ kind, key }: FileTypeOption) => `${kind}:${key}`;

// The API has no description field on documents, so the description is saved as the filename.
const withDescriptionAsName = (file: File, description: string) => {
  const dotIndex = file.name.lastIndexOf('.');
  const extension = dotIndex > 0 ? file.name.slice(dotIndex) : '';
  return new File([file], `${description.trim()}${extension}`, { type: file.type });
};

const UploadFileModal = ({
  isOpen,
  onClose,
  checklist,
  upload,
  isProcessing,
  extraDocumentOptions = [],
}: UploadFileModalProps) => {
  const [selectedValue, setSelectedValue] = useState('');
  const [description, setDescription] = useState('');

  const photosSection = findChecklistSection(checklist, 'photos');
  const documentOptions: FileTypeOption[] = [
    ...(findChecklistSection(checklist, 'documents')?.items ?? []).map(
      ({ documentType, label }): FileTypeOption => ({ kind: 'document', key: documentType, label }),
    ),
    ...extraDocumentOptions,
  ];
  const photoOptions: FileTypeOption[] = photosSection
    ? [...photosSection.shots, { photoType: 'odometer', label: photosSection.odometer.label }].map(
        ({ photoType, label }): FileTypeOption => ({ kind: 'photo', key: photoType, label }),
      )
    : [];

  const [selectedKind, selectedKey] = selectedValue.split(':') as [UploadKind, string?];
  const selectedDocumentOption = documentOptions.find(
    (option) => optionValue(option) === selectedValue,
  );
  const selectedHint = selectedDocumentOption?.hint;
  const isOther = selectedValue === optionValue(OTHER_OPTION);
  const isUploading = !!selectedKey && isProcessing(selectedKey);
  const canChooseFile = !!selectedKey && (!isOther || description.trim().length > 0);

  const handleClose = () => {
    setSelectedValue('');
    setDescription('');
    onClose();
  };

  const { fileInputRef, openPicker, handleFileSelected } = useFilePicker(async (file) => {
    if (!selectedKey) return;
    const uploaded = await upload(
      selectedKind,
      selectedKey,
      isOther ? withDescriptionAsName(file, description) : file,
      selectedDocumentOption?.dealOnlyMediaType,
    );
    if (uploaded) handleClose();
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Upload a file"
      titleFontSize="22px"
      titleFontWeight={700}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept={selectedKey ? acceptForKind(selectedKind) : undefined}
        hidden
        onChange={handleFileSelected}
      />
      <Flex direction="column" gap="14px" pb={4}>
        <Select
          placeholder="What is this file?"
          value={selectedValue}
          onChange={(e) => setSelectedValue(e.target.value)}
        >
          {documentOptions.length > 0 && (
            <optgroup label="Documents">
              {documentOptions.map((option) => (
                <option key={optionValue(option)} value={optionValue(option)}>
                  {option.label}
                </option>
              ))}
            </optgroup>
          )}
          {photoOptions.length > 0 && (
            <optgroup label="Photos of your car">
              {photoOptions.map((option) => (
                <option key={optionValue(option)} value={optionValue(option)}>
                  {option.label}
                </option>
              ))}
            </optgroup>
          )}
          <option value={optionValue(OTHER_OPTION)}>{OTHER_OPTION.label}</option>
        </Select>
        {selectedHint && (
          <Text fontSize="13px" color="grayMedium" lineHeight="1.5">
            {selectedHint}
          </Text>
        )}
        {isOther && (
          <Input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Briefly describe what this file is"
            maxLength={100}
          />
        )}
        <Center>
          <PrimaryButton
            value="CHOOSE FILE"
            onClick={openPicker}
            isDisabled={!canChooseFile}
            loading={isUploading}
          />
        </Center>
      </Flex>
    </Modal>
  );
};

export default UploadFileModal;
